// Angular Core
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule, HammerGestureConfig, HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';
import { NgModule, Injectable, APP_INITIALIZER } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { RouterModule } from '@angular/router';

// Routing
import { AppRoutes } from './app.routing';
import { AppComponent } from './app.component';

// Layouts
import {
  // CondensedComponent,
  // BlankComponent,
  // RootLayout,
  // CorporateLayout,
  SimplyWhiteLayout,
  // ExecutiveLayout,
  // CasualLayout,
  // DemoComponent
} from './@pages/layouts';
// Layout Service - Required
import { pagesToggleService } from './@pages/services/toggler.service';

// Shared Layout Components
import { SidebarComponent } from './@pages/components/sidebar/sidebar.component';
import { QuickviewComponent } from './@pages/components/quickview/quickview.component';
import { QuickviewService } from './@pages/components/quickview/quickview.service';
import { SearchOverlayComponent } from './@pages/components/search-overlay/search-overlay.component';
import { HeaderComponent } from './@pages/components/header/header.component';
import { HorizontalMenuComponent } from './@pages/components/horizontal-menu/horizontal-menu.component';
import { SharedModule } from './@pages/components/shared.module';
import { pgListViewModule } from './@pages/components/list-view/list-view.module';
import { pgCardModule } from './@pages/components/card/card.module';
import { pgCardSocialModule } from './@pages/components/card-social/card-social.module';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { pgSelectModule } from './@pages/components/select/select.module';
import { NgPipesModule } from 'ngx-pipes';

// Pages Globaly required Components - Optional
import { pgTabsModule } from './@pages/components/tabs/tabs.module';
import { ProgressModule } from './@pages/components/progress/progress.module';

// Thirdparty Components / Plugins - Optional
// import { NvD3Module } from 'ngx-nvd3';
// import { NgxEchartsModule } from 'ngx-echarts';
// import { IsotopeModule } from 'ngx-isotope';
// import { StepsformDirective } from './social/stepsform.directive';
import { NgScrollbarModule } from 'ngx-scrollbar';

// Service - Demo content - Optional
// import { ChartService } from './charts/charts.service';
// import { SocialService } from './social/social.service';

// Social Page - Optional
// import { SocialComponent } from './social/social.component';
// import { CoverpageDirective } from './social/coverpage.directive';

// Demo Pages - Optional
// import { FormWizardComponent } from './forms/form-wizard/form-wizard.component';
import { CardsPageComponent } from './cards/cards.component';
import { ViewsPageComponent } from './views/views.component';
// import { ChartsComponent } from './charts/charts.component';

// Dashboard Widgets - Optional
import { DashboardModule } from './dashboard/dashboard.module';

// Dashboards - Optional
// import { CondensedDashboardComponent } from './dashboard/condensed/dashboard.component';
import { SimplyWhiteDashboardComponent } from './dashboard/simplywhite/dashboard.component';
// import { CasualDashboardComponent } from './dashboard/casual/dashboard.component';
// import { CorporateDashboardComponent } from './dashboard/corporate/dashboard.component';
// import { ExecutiveDashboardComponent } from './dashboard/executive/dashboard.component';

// Sample Blank Pages - Optional
// import { BlankCorporateComponent } from './@pages/layouts/blank-corporate/blank-corporate.component';
import { BlankSimplywhiteComponent } from './@pages/layouts/blank-simplywhite/blank-simplywhite.component';
// import { BlankCasualComponent } from './@pages/layouts/blank-casual/blank-casual.component';
import { HomeComponent } from './home/home.component';

// Widgets
import { WidgetsModule } from './widgets/widgets.module';
import { JwtInterceptor } from './_helpers/jwt.interceptor';

// Bootstrap
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { AlertModule } from 'ngx-bootstrap/alert';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { AuthenticationService } from './_services/authentication.service'; // adjust the path as needed
import { lastValueFrom } from 'rxjs';
import { MessageModule } from './@pages/components/message/message.module';
import { MessageService } from './@pages/components/message/message.service';
import { _MESSAGE_DEFAULT_CONFIG } from './@pages/components/message/message-config';
import { NetsuiteService } from './_services/netsuite.service';

// const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
//   suppressScrollX: true
// };

export function initializeApp(authService: AuthenticationService, netsuiteService: NetsuiteService) {
  console.log('initializeApp');
  return () => Promise.all([
    lastValueFrom(authService.loadConfig()),
    lastValueFrom(netsuiteService.loadConfig()),
  ]);
}

// Hammer Config Overide
// https://github.com/angular/angular/issues/10541
@Injectable()
export class AppHammerConfig extends HammerGestureConfig {
  overrides = <any>{
    pinch: { enable: false },
    rotate: { enable: false },
  };
}

@NgModule({
  declarations: [
    AppComponent,
    // CondensedComponent,
    // CorporateLayout,
    SimplyWhiteLayout,
    // ExecutiveLayout,
    // CasualLayout,
    SidebarComponent,
    QuickviewComponent,
    SearchOverlayComponent,
    HeaderComponent,
    HorizontalMenuComponent,
    // BlankComponent,
    // DemoComponent, //Remove in production
    // RootLayout,
    CardsPageComponent,
    ViewsPageComponent,
    // ChartsComponent,
    // SocialComponent,
    // StepsformDirective,
    // CoverpageDirective,
    // CondensedDashboardComponent,
    SimplyWhiteDashboardComponent,
    // CasualDashboardComponent,
    // CorporateDashboardComponent,
    // ExecutiveDashboardComponent,
    // BlankCorporateComponent,
    BlankSimplywhiteComponent,
    // BlankCasualComponent,
    HomeComponent,
  ],
  imports: [
    DragDropModule,
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    FormsModule,
    SharedModule,
    ProgressModule,
    pgListViewModule,
    pgCardModule,
    pgSelectModule,
    pgCardSocialModule,
    RouterModule.forRoot(AppRoutes, {}),
    BsDropdownModule.forRoot(),
    AccordionModule.forRoot(),
    AlertModule.forRoot(),
    ButtonsModule.forRoot(),
    CollapseModule.forRoot(),
    ModalModule.forRoot(),
    ProgressbarModule.forRoot(),
    TabsModule.forRoot(),
    TooltipModule.forRoot(),
    TypeaheadModule.forRoot(),
    MessageModule,
    // NvD3Module,
    pgTabsModule,
    // NgxEchartsModule,
    // IsotopeModule,
    // NgxDnDModule,
    NgxDatatableModule,
    // PerfectScrollbarModule,
    NgScrollbarModule,
    DashboardModule,
    NgPipesModule,
    WidgetsModule,
  ],
  providers: [
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    MessageService,
    {
      provide: _MESSAGE_DEFAULT_CONFIG,
      useValue: {
        Position: 'top-right',
        Style: 'flip',
        Duration: 5000,
        Animate: true,
        PauseOnHover: true,
        MaxStack: 10,
      },
    },
    QuickviewService,
    pagesToggleService,
    // ChartService,
    // SocialService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    // {
    //   provide: PERFECT_SCROLLBAR_CONFIG,
    //   useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG
    // },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: AppHammerConfig,
    },
    AuthenticationService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AuthenticationService, NetsuiteService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
